import React, { createContext, useContext, useState, useEffect } from "react";
import { useAuth } from "@/lib/auth";

export type SimulationRole = "admin" | "client" | "project_owner";

interface SimulationContextType {
  simulationRole: SimulationRole;
  simulatedUserId: string | null;
  simulatedAccountId: string | null;
  simulatedUserName: string | null;
  setSimulationRole: (role: SimulationRole) => void;
  startSimulation: (role: SimulationRole, userId?: string | null, accountId?: string | null, userName?: string | null) => void;
  isInSimulation: boolean;
}

const SimulationContext = createContext<SimulationContextType | undefined>(undefined);

export function SimulationProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const [simulationRole, setSimulationRole] = useState<SimulationRole>("admin");
  const [simulatedUserId, setSimulatedUserId] = useState<string | null>(null);
  const [simulatedAccountId, setSimulatedAccountId] = useState<string | null>(null);
  const [simulatedUserName, setSimulatedUserName] = useState<string | null>(null);

  // Reset simulation when user role changes or logout
  useEffect(() => {
    stopSimulation();
  }, [user?.role, user?.id]);

  const startSimulation = (role: SimulationRole, userId: string | null = null, accountId: string | null = null, userName: string | null = null) => {
    setSimulationRole(role);
    setSimulatedUserId(userId);
    setSimulatedAccountId(accountId);
    setSimulatedUserName(userName);
  };

  const stopSimulation = () => {
    setSimulationRole("admin");
    setSimulatedUserId(null);
    setSimulatedAccountId(null);
    setSimulatedUserName(null);
  };

  const isInSimulation = simulationRole !== "admin";

  return (
    <SimulationContext.Provider value={{ 
      simulationRole, 
      simulatedUserId, 
      simulatedAccountId, 
      simulatedUserName,
      setSimulationRole: (role) => startSimulation(role), 
      startSimulation,
      isInSimulation 
    }}>
      {children}
    </SimulationContext.Provider>
  );
}

export function useSimulation() {
  const context = useContext(SimulationContext);
  if (context === undefined) {
    throw new Error("useSimulation must be used within a SimulationProvider");
  }
  return context;
}
